import { z } from "zod";

// Common validation rules
const nameField = z
  .string()
  .trim()
  .min(1, "Ce champ est requis")
  .max(100, "Maximum 100 caractères")
  .regex(/^[a-zA-ZÀ-ÿ\s\-']+$/, "Caractères non autorisés");

const cityField = z
  .string()
  .trim()
  .min(1, "Ce champ est requis")
  .max(100, "Maximum 100 caractères");

const emailField = z
  .string()
  .trim()
  .email("Email invalide")
  .max(255, "Maximum 255 caractères");

const passwordField = z
  .string()
  .min(10, "Minimum 10 caractères")
  .max(128, "Maximum 128 caractères")
  .regex(/[A-Z]/, "Doit contenir au moins une majuscule")
  .regex(/[a-z]/, "Doit contenir au moins une minuscule")
  .regex(/[0-9]/, "Doit contenir au moins un chiffre");

const textField = z
  .string()
  .trim()
  .max(500, "Maximum 500 caractères")
  .optional()
  .transform((val) => val || undefined);

const longTextField = z
  .string()
  .trim()
  .max(2000, "Maximum 2000 caractères")
  .optional()
  .transform((val) => val || undefined);

// Age field with minimum 15 years validation
const ageField = z
  .number({ invalid_type_error: "L'âge doit être un nombre" })
  .int("L'âge doit être un nombre entier")
  .min(15, "Inscription impossible : les entreprises ne peuvent légalement pas recruter des jeunes de moins de 15 ans pour un job.")
  .max(120, "Âge invalide");

// Student registration schema
export const studentRegistrationSchema = z.object({
  firstName: nameField,
  lastName: nameField,
  email: emailField,
  city: cityField,
  age: ageField,
  password: passwordField,
  consent: z.literal(true, {
    errorMap: () => ({ message: "Vous devez accepter les conditions" }),
  }),
});

export type StudentRegistrationInput = z.infer<typeof studentRegistrationSchema>;

// Profile completion schema (for existing users without age)
export const profileCompletionSchema = z.object({
  age: ageField,
});

// Employer registration schema
export const employerRegistrationSchema = z.object({
  companyName: z
    .string()
    .trim()
    .min(1, "Ce champ est requis")
    .max(200, "Maximum 200 caractères"),
  sector: z
    .string()
    .trim()
    .min(1, "Ce champ est requis")
    .max(100, "Maximum 100 caractères"),
  city: cityField,
  email: emailField,
  description: longTextField,
  password: passwordField,
  consent: z.literal(true, {
    errorMap: () => ({ message: "Vous devez accepter les conditions" }),
  }),
});

export type EmployerRegistrationInput = z.infer<typeof employerRegistrationSchema>;

// Student profile update schema
export const studentProfileSchema = z.object({
  first_name: nameField.optional(),
  city: cityField.optional(),
  availability: textField,
});

export type StudentProfileInput = z.infer<typeof studentProfileSchema>;

// Employer profile update schema
export const employerProfileSchema = z.object({
  company_name: z
    .string()
    .trim()
    .max(200, "Maximum 200 caractères")
    .optional()
    .transform((val) => val || undefined),
  city: z
    .string()
    .trim()
    .max(100, "Maximum 100 caractères")
    .optional()
    .transform((val) => val || undefined),
  sector: z
    .string()
    .trim()
    .max(100, "Maximum 100 caractères")
    .optional()
    .transform((val) => val || undefined),
  description: longTextField,
  business_registration_number: z
    .string()
    .trim()
    .max(50, "Maximum 50 caractères")
    .optional()
    .transform((val) => val || undefined),
  website_url: z
    .string()
    .trim()
    .max(500, "Maximum 500 caractères")
    .optional()
    .transform((val) => val || undefined)
    .refine(
      (val) => !val || /^https?:\/\/.+/.test(val),
      "L'URL doit commencer par http:// ou https://"
    ),
});

export type EmployerProfileInput = z.infer<typeof employerProfileSchema>;

// Job creation schema
export const jobSchema = z.object({
  title: z
    .string()
    .trim()
    .min(1, "Le titre est requis")
    .max(200, "Maximum 200 caractères"),
  job_type: z.enum(["job-etudiant", "job-ete", "stage"], {
    errorMap: () => ({ message: "Type de contrat invalide" }),
  }),
  location: cityField,
  schedule: textField,
  description: longTextField,
  hourly_rate: z
    .number()
    .min(0, "Le tarif doit être positif")
    .max(1000, "Tarif maximum 1000€")
    .optional(),
});

export type JobInput = z.infer<typeof jobSchema>;

// Application schema
export const applicationSchema = z.object({
  job_id: z.string().uuid("ID de l'offre invalide"),
  message: z
    .string()
    .trim()
    .max(1000, "Maximum 1000 caractères")
    .optional()
    .transform((val) => val || undefined),
});

export type ApplicationInput = z.infer<typeof applicationSchema>;

// Validation helper function
export function validateInput<T>(
  schema: z.ZodSchema<T>,
  data: unknown
): { success: true; data: T } | { success: false; errors: string[] } {
  const result = schema.safeParse(data);
  
  if (result.success) {
    return { success: true, data: result.data };
  }
  
  const errors = result.error.errors.map((err) => 
    `${err.path.join(".")}: ${err.message}`
  );
  
  return { success: false, errors };
}

// Helper to get first error message from Zod result
export function getFirstError(result: z.SafeParseReturnType<unknown, unknown>): string | null {
  if (result.success) return null;
  return result.error.errors[0]?.message || "Erreur de validation";
}
